import React, { useEffect } from 'react';
import TinyMCE from 'tinymce/localized_editor';
import TinyMCEConfig from 'tinymce/config';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Label, SectionTitle } from '@grnhse/seedling/lib/birch/typography';
import SingleSelectField from '@grnhse/seedling/lib/birch/components/forms/fields/single_select_field';
import TextField from '@grnhse/seedling/lib/birch/components/forms/fields/text_field';
import { SimpleSingleDropdown } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { withLocalePrefix } from 'shared/utils/translation';
import { SelectionsHelper } from 'talent_matching/hooks/use_selections_helper';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import { RejectModalOptionsResponse } from 'candidate_profile_redesign/shared/types';
import { useEmailTemplate } from 'talent_matching/hooks/use_email_template';
import CheckboxField from '@grnhse/seedling/lib/birch/components/forms/fields/checkbox_field';
import { PaginatedMultiDropdown } from 'candidate_profile_redesign/shared/form/Fields';
import SendLaterRow from 'candidate_profile_redesign/modals/email/send_later_row';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import UnverifiedDomainCallout from 'talent_matching/components/bulk_actions/send_email/unverified_domain_callout';
import BulkRejectionCallout from 'talent_matching/components/bulk_actions/reject/bulk_rejection_callout';

const t = withLocalePrefix('talent_matching.bulk_actions.reject');

type Props = {
  selectionsHelper: SelectionsHelper;
  rejectionData: RejectModalOptionsResponse;
  shouldDisplayEmailDomainWarning: boolean;
};

const EmailSection = ({
  selectionsHelper,
  rejectionData,
  shouldDisplayEmailDomainWarning,
}: Props) => {
  const { watch, setValue } = useFormContext();
  const [htmlBody, sendEmail, template, from] = watch([
    'html_body',
    'send_email',
    'template',
    'from',
  ]);

  const { data: emailTemplate, isFetching } = useEmailTemplate(template?.value);

  useEffect(() => {
    if (emailTemplate) {
      const fromValue = rejectionData.email.fromOptions.find(
        (option) => option.value === emailTemplate.from
      );
      setValue('from', fromValue);
      setValue('subject', emailTemplate.subject);
      setValue('html_body', emailTemplate.html_body);
      setValue(
        'cc',
        emailTemplate.cc.map((email: string) => ({
          label: email,
          value: email,
        }))
      );
      setValue('cc_recruiter', emailTemplate.cc_recruiter);
      setValue('cc_coordinator', emailTemplate.cc_coordinator);
      setValue('include_self', false);
    } else {
      setValue('from', '');
      setValue('subject', '');
      setValue('html_body', '');
      setValue('cc', []);
      setValue('cc_recruiter', false);
      setValue('cc_coordinator', false);
      setValue('include_self', false);
    }
  }, [emailTemplate, setValue, rejectionData.email.fromOptions]);

  if (!rejectionData.email.canSend) {
    return null;
  }

  if (!sendEmail) {
    return null;
  }

  const displayCallout =
    shouldDisplayEmailDomainWarning && from?.domain_unverified === true;

  return (
    <Stack
      spacing={Tokens.spacing['2']}
      sx={{ marginBottom: Tokens.spacing['1.5'] }}
    >
      <Divider />
      <SectionTitle>{t('labels.rejection_email')}</SectionTitle>
      <SendLaterRow
        whenEmailOptions={rejectionData.email.sendAt.whenEmailOptions}
        customTimeOptions={rejectionData.email.sendAt.customTimeOptions}
        dateFormatBirchPicker={rejectionData.dateFormatBirchPicker}
        timezone={rejectionData.email.sendAt.timezone}
        shouldValidate={sendEmail}
      />
      <SingleSelectField
        name="template"
        label={t('labels.template')}
        items={rejectionData.email.templateOptions}
        disabled={isFetching}
      />
      <BulkRejectionCallout />
      <SingleSelectField
        name="from"
        label={t('labels.from')}
        items={rejectionData.email.fromOptions}
        clearable={false}
        disabled={isFetching}
        required
      />
      {displayCallout && (
        <UnverifiedDomainCallout domain={from?.domain || ''} />
      )}
      <SimpleSingleDropdown
        items={[]}
        onSelectedItemChange={() => null}
        selectedItem={{
          value: `${selectionsHelper.totalSelected} candidates`,
          label: `${selectionsHelper.totalSelected} candidates`,
        }}
        label={t('labels.to')}
        readOnly={true}
        disabled={isFetching}
      />
      <PaginatedMultiDropdown
        getOptionsUrl={rejectionData.getCcOptionsPath}
        name="cc"
        label={t('labels.cc')}
        disabled={isFetching}
      />
      <Stack direction="row" spacing={Tokens.spacing['2']}>
        <CheckboxField
          name="cc_recruiter"
          label={t('labels.cc_recruiter')}
          value="cc_recruiter"
          disabled={isFetching}
        />
        <CheckboxField
          name="cc_coordinator"
          label={t('labels.cc_coordinator')}
          value="cc_coordinator"
          disabled={isFetching}
        />
        <CheckboxField
          name="include_self"
          label={t('labels.include_self')}
          value="include_self"
          disabled={isFetching}
        />
      </Stack>
      <TextField
        name="subject"
        label={t('labels.subject')}
        rules={{ required: t('labels.subject_required') }}
        disabled={isFetching}
      />
      <Label>{t('labels.body')}</Label>
      <TinyMCE
        init={{
          ...TinyMCEConfig.Email,
          selector: undefined,
        }}
        onEditorChange={(content) => {
          setValue('html_body', content);
        }}
        value={htmlBody}
        disabled={isFetching}
      />
    </Stack>
  );
};

export default EmailSection;
